// SETTINGS V2 — location row kebab menu (SETTINGS_V2_SP4 §5.3 / SP4.3 Fix 3).
// Extracted from LocationRowV2 to keep the row < 300 lines. Radix dropdown with
// the SP3 portal scope-class trick. Items: Set-as-default (parents, non-default),
// Deaktivovať / Aktivovať.
//
// SP4.3 Fix 3: the server refuses to disable the DEFAULT location (raw English
// backend message reached the user). Client-side we now render the "Deaktivovať"
// item DISABLED (greyed, NOT hidden — discoverability) with a short localized
// sub-line explaining why, instead of letting the user fire a doomed request.
// The server stays authoritative; this is a UX guard only. Inline styles + scoped
// CSS-var tokens + Radix. No MUI/sx/dt/Tailwind. SEC-REQ-2: no side effects.
//
// LOC-DELETE (SETTINGS_V2_LOC_DELETE_SPEC §7.2): a danger "Vymazať natrvalo"
// item, LAST in the menu, separated below Deaktivovať/Aktivovať, shown for BOTH
// enabled and disabled rows and for child rows. Rendered ONLY for PartnerHub
// Admins (usePermission client gate — SEC-REQ-D8, a DELIBERATE deviation from
// SP4's "no client gate" parity; the server gate stays authoritative).
import React from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";

import { useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import usePermission from "../../../../shared/hooks/usePermission";
import { Modules } from "../../../../core/models/PermissionModels";
import { SettingsV2Tooltip } from "../atoms";
import type { Location } from "../../../machinery/components/locations/locationTypes";

export interface LocationRowV2MenuProps {
  location: Location;
  isChild: boolean;
  onSetDefault?: (location: Location) => void;
  onDisable: (location: Location) => void;
  onDelete?: (location: Location) => void;
  busy?: boolean;
}

const LocationRowV2Menu: React.FC<LocationRowV2MenuProps> = ({
  location,
  isChild,
  onSetDefault,
  onDisable,
  onDelete,
  busy,
}) => {
  const { translate: t } = useTranslation("settings");
  const theme = useAppSelector((s) => s.settingsV2Ui.theme);
  const { isPhone } = useBreakpoint();
  // Client gate: the hard-delete affordance is Admin-only (SEC-REQ-D8). The
  // server DELETE stays authoritative regardless.
  const { canAdmin } = usePermission(Modules.PartnerHub);

  // A default location that is NOT already disabled must not be deactivated
  // (server forbids it). Show the item greyed with an explanation rather than
  // hidden. If it were somehow both default AND disabled, Aktivovať stays live.
  const blockDisable = location.isDefault && !location.isDisabled;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="btn btn-ghost"
          aria-label={t("settings:actions")}
          disabled={busy}
          style={{
            width: isPhone ? 44 : 30,
            minWidth: isPhone ? 44 : 30,
            height: isPhone ? 44 : 30,
            padding: 0,
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          ⋮
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={`settings-v2-root p-${theme} mod-nastavenia sv2-menu`}
          align="end"
          sideOffset={4}
        >
          {!isChild && !location.isDefault && onSetDefault && (
            <DropdownMenu.Item className="sv2-menu-item" onSelect={() => onSetDefault(location)}>
              {t("settings:locations.setAsDefault")}
            </DropdownMenu.Item>
          )}

          {location.isDisabled ? (
            // Re-enable — always available (default ink).
            <SettingsV2Tooltip text={t("settings:locations.tooltipEnable")}>
              <DropdownMenu.Item className="sv2-menu-item" onSelect={() => onDisable(location)}>
                {t("settings:locations.enable")}
              </DropdownMenu.Item>
            </SettingsV2Tooltip>
          ) : blockDisable ? (
            // Deaktivovať blocked for the default location — greyed, disabled,
            // with a localized reason sub-line (Fix 3).
            <DropdownMenu.Item
              className="sv2-menu-item"
              disabled
              style={{
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 3,
                color: "var(--faint)",
                cursor: "default",
              }}
            >
              <span>{t("settings:locations.disable")}</span>
              <span
                style={{
                  fontSize: 10.5,
                  lineHeight: 1.35,
                  color: "var(--faint)",
                  whiteSpace: "normal",
                  maxWidth: 210,
                }}
              >
                {t("settings:locations.cannotDisableDefault")}
              </span>
            </DropdownMenu.Item>
          ) : (
            // Normal Deaktivovať — painted red.
            <SettingsV2Tooltip text={t("settings:locations.tooltipDisable")}>
              <DropdownMenu.Item
                className="sv2-menu-item"
                onSelect={() => onDisable(location)}
                style={{ color: "var(--red)" }}
              >
                {t("settings:locations.disable")}
              </DropdownMenu.Item>
            </SettingsV2Tooltip>
          )}

          {/* Vymazať natrvalo (§7.2) — permanent hard delete. Admin-only client
              gate; last item; separated; danger red. Shown for enabled AND
              disabled rows, and for child rows. */}
          {canAdmin && onDelete && (
            <>
              <DropdownMenu.Separator
                style={{
                  height: 1,
                  margin: "4px 0",
                  background: "var(--border-soft)",
                }}
              />
              <SettingsV2Tooltip text={t("settings:locations.tooltipDelete")}>
                <DropdownMenu.Item
                  className="sv2-menu-item"
                  onSelect={() => onDelete(location)}
                  style={{ color: "var(--red)" }}
                >
                  {t("settings:locations.deletePermanently")}
                </DropdownMenu.Item>
              </SettingsV2Tooltip>
            </>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export default LocationRowV2Menu;
